import $axios from 'axios'
import { COOKIE_DURATION, cookieOpts } from '@/utils/cookieOptions'
import {
  clearVehicleHistory,
  readVehicleHistory,
  vehicleHistoryKey,
  writeVehicleHistory,
} from '@/utils/vehicleHistoryStorage'
import { isProCustomer, paymentOffers } from '@/utils/paymentOffers'
import { freeShippingOfferFor } from '@/utils/freeShippingOffer'
import { isManualBannerVisible, welcomeOfferFor } from '@/utils/welcomeOffer'
import { previewNow } from '@/utils/clockChange'
import { operationBannerPhase } from '@/utils/commercialOperation'
import { isHalloweenPeriod } from '@/utils/halloween'
import { isIsoCountry } from '@/utils/priceCountry'

const isDev = process.env.NODE_ENV !== 'production'
const devError = (...args) => isDev && console.error(...args)

const HISTORY_COOKIE_NAME = 'vehicle_search_history'
const SELECTED_VEHICLE_COOKIE_NAME = 'selected_vehicle'
const HISTORY_MAX_ITEMS = 10

const cleanImageForStorage = image =>
  image?.url
    ? {
        url: image.url,
        url_template: image.url_template || null,
        extension: image.extension || null,
      }
    : null

const cleanVehicleForStorage = vehicle => ({
  id: vehicle.id,
  name: vehicle.name,
  search: vehicle.search || null,
  brand: vehicle.brand
    ? {
        name: vehicle.brand.name,
        image: cleanImageForStorage(vehicle.brand.image),
      }
    : null,
  image: cleanImageForStorage(vehicle.image),
  vehicle_type: vehicle.vehicle_type?.identifier
    ? { identifier: vehicle.vehicle_type.identifier }
    : null,
  year_from: vehicle.year_from || null,
  year_to: vehicle.year_to || null,
  autowaysLookupId: vehicle.autowaysLookupId || null,
})

export default {
  state: () => ({
    cart: null,
    banner: null,
    banner_start: null,
    banner_end: null,
    clock_now: null,
    clock_offset: 0,
    clock_country: null,
    clock_change_enabled: true,
    halloween_enabled: false,
    clock_change_hidden: null,
    welcome_offer: null,
    commercial_operation: null,
    products: [],
    domains: [],
    domainsLoading: false,
    domainSubmenus: {},
    domainSubmenusLoading: {},
    country: 'FR',
    chosen_country: null,
    country_resolved_ssr: false,
    product: {},
    referrer: null,
    loading_products: false,
    total_products: 0,
    search_vehicle_packs: 0,
    pagination: {
      page: 1,
      limit: 5,
      total: 0,
    },
    message: null,
    message_compte_client: null,
    filters_product_selected: {},
    product_added_to_cart: {},
    last_filter_selected: null,
    unknown_registration: {
      id: null,
      license_plate_used: null,
      email: false,
    },
    vat_country: '',
    video: null,
    vat_rate: null,
    free_shipping: null,
    free_shipping_thresholds: {},
    free_shipping_method_thresholds: {},
    store_opening_hours: null,
    payment_methods: [],
    favorites: [],
    selected_vehicle: null,
    selected_vehicle_restored: false,
    vehicle_search_history: [],
    vehicle_selection_target: null,
    cart_load_params: null,
    alternate_urls: {},
    restricted_locales: [],
  }),

  getters: {
    payment_offers(state) {
      const user = state.auth?.loggedIn ? state.auth.user : null

      return paymentOffers({
        paymentMethods: state.payment_methods,
        country: user?.country?.iso || state.country,
        isPro: isProCustomer(user),
      })
    },
    free_shipping_offer_for: state => country =>
      freeShippingOfferFor({
        country,
        frenchThreshold: state.free_shipping,
        thresholds: state.free_shipping_thresholds,
      }),
    free_shipping_offer(state, getters) {
      const user = state.auth?.loggedIn ? state.auth.user : null

      return getters.free_shipping_offer_for(
        user?.country?.iso || state.country
      )
    },
    free_shipping_method_thresholds_for: state => country =>
      state.free_shipping_method_thresholds?.[country] || null,
    store_opening_week(state) {
      return state.store_opening_hours?.week || null
    },
    is_pro(state) {
      return state.auth.loggedIn && state.auth.user?.company
    },
    is_pro_plus(state) {
      return (
        state.auth.loggedIn &&
        state.auth.user?.company &&
        state.auth.user?.company?.verified_at
      )
    },
    manual_banner_visible(state) {
      return isManualBannerVisible({
        banner: state.banner,
        start: state.banner_start,
        end: state.banner_end,
      })
    },
    operation_banner_phase(state) {
      return operationBannerPhase(state.commercial_operation, state.clock_now)
    },
    welcome_offer_for_visitor(state, getters) {
      return welcomeOfferFor({
        offer: state.welcome_offer,
        loggedIn: state.auth.loggedIn,
        user: state.auth.user,
        isProPlus: Boolean(getters.is_pro_plus),
      })
    },
    show_vat(state) {
      return state.auth.loggedIn && state.auth.user?.country?.should_collect_vat
    },
    cart(state) {
      return state.cart
    },
    message(state) {
      return state.message
    },
    product_added_to_cart(state) {
      return state.product_added_to_cart
    },
    referrer(state) {
      return state.referrer
    },
    product(state) {
      return state.product
    },
    favorites_list(state) {
      return state.favorites
    },
    selected_vehicle(state) {
      return state.selected_vehicle
    },
    selected_vehicle_restored(state) {
      return state.selected_vehicle_restored
    },
    vehicle_search_history(state) {
      return state.vehicle_search_history
    },
    vehicle_selection_target(state) {
      return state.vehicle_selection_target
    },
    cart_load_params(state) {
      return state.cart_load_params
    },
    pagination(state) {
      return state.pagination
    },
    total_products(state) {
      return state.total_products
    },
    search_vehicle_packs(state) {
      return state.search_vehicle_packs
    },
    products_list(state) {
      return state.products
    },
    domains(state) {
      return state.domains
    },
    video(state) {
      return state.video
    },
    extra_data(state) {
      return state.extraData
    },
    filters_product_selected(state) {
      return state.filters_product_selected
    },
    last_filter_selected(state) {
      return state.last_filter_selected
    },
    loading_products(state) {
      return state.loading_products
    },
    country(state) {
      return state.country
    },
  },

  mutations: {
    SET_ALTERNATE_URLS(state, urls) {
      state.alternate_urls = urls
    },
    SET_RESTRICTED_LOCALES(state, codes) {
      state.restricted_locales = Array.isArray(codes) ? codes : []
    },
    SET_COUNTRY(state, country) {
      state.country = country
    },
    SET_CHOSEN_COUNTRY(state, country) {
      state.chosen_country = country
    },
    SET_COUNTRY_RESOLVED_SSR(state, resolved) {
      state.country_resolved_ssr = Boolean(resolved)
    },
    SET_FREE_SHIPPING(state, free_shipping) {
      state.free_shipping = free_shipping
    },
    SET_FREE_SHIPPING_THRESHOLDS(state, thresholds) {
      state.free_shipping_thresholds = thresholds
    },
    SET_FREE_SHIPPING_METHOD_THRESHOLDS(state, thresholds) {
      state.free_shipping_method_thresholds = thresholds
    },
    SET_STORE_OPENING_HOURS(state, store_opening_hours) {
      state.store_opening_hours = store_opening_hours
    },
    SET_PAYMENT_METHODS(state, payment_methods) {
      state.payment_methods = payment_methods
    },
    SET_VAT_COUNTRY(state, vat_country) {
      state.vat_country = vat_country
    },
    SET_VAT_RATE(state, vat_rate) {
      state.vat_rate = vat_rate
    },
    SET_CART(state, cart) {
      state.cart = cart
    },
    SET_BANNER(state, banner) {
      state.banner = banner
    },
    SET_BANNER_URL(state, banner_url) {
      state.banner_url = banner_url
    },
    SET_BANNER_START(state, banner_start) {
      state.banner_start = banner_start
    },
    SET_BANNER_END(state, banner_end) {
      state.banner_end = banner_end
    },
    SET_CLOCK_NOW(state, now) {
      state.clock_now = now
    },
    SET_CLOCK_OFFSET(state, offset) {
      state.clock_offset = offset
    },
    SET_CLOCK_COUNTRY(state, country) {
      state.clock_country = country
    },
    SET_CLOCK_CHANGE_ENABLED(state, enabled) {
      state.clock_change_enabled = enabled
    },
    SET_CLOCK_CHANGE_HIDDEN(state, key) {
      state.clock_change_hidden = key
    },
    SET_HALLOWEEN_ENABLED(state, enabled) {
      state.halloween_enabled = enabled
    },
    SET_COMMERCIAL_OPERATION(state, operation) {
      state.commercial_operation = operation
    },
    SET_WELCOME_OFFER(state, welcome_offer) {
      state.welcome_offer = welcome_offer
    },
    SET_FAVORITES(state, favorites) {
      state.favorites = favorites
    },
    SET_SELECTED_VEHICLE(state, vehicle) {
      state.selected_vehicle = vehicle
    },
    SET_SELECTED_VEHICLE_RESTORED(state) {
      state.selected_vehicle_restored = true
    },
    SET_VEHICLE_SELECTION_TARGET(state, target) {
      state.vehicle_selection_target = target
    },
    SET_CART_LOAD_PARAMS(state, params) {
      state.cart_load_params = params
    },
    SET_VEHICLE_SEARCH_HISTORY(state, history) {
      state.vehicle_search_history = history.slice(0, HISTORY_MAX_ITEMS)
    },
    ADD_TO_VEHICLE_SEARCH_HISTORY(state, vehicle) {
      const normalizedVehicle = {
        ...vehicle,
        brand: {
          name: vehicle.brand?.name || null,
          image: vehicle.brand?.image || null,
        },
        image: vehicle.image || null,
      }

      const vehicleKey = vehicleHistoryKey(vehicle)
      const existingIndex = state.vehicle_search_history.findIndex(
        v => vehicleHistoryKey(v) === vehicleKey
      )

      if (existingIndex !== -1) {
        state.vehicle_search_history.splice(existingIndex, 1)
      }

      state.vehicle_search_history.unshift(normalizedVehicle)

      if (state.vehicle_search_history.length > HISTORY_MAX_ITEMS) {
        state.vehicle_search_history = state.vehicle_search_history.slice(
          0,
          HISTORY_MAX_ITEMS
        )
      }
    },
    REMOVE_FROM_VEHICLE_SEARCH_HISTORY(state, { vehicleId, search }) {
      state.vehicle_search_history = state.vehicle_search_history.filter(v => {
        if (search !== undefined && search !== null) {
          return !(v.id === vehicleId && v.search === search)
        }
        return v.id !== vehicleId
      })
    },
    CLEAR_VEHICLE_SEARCH_HISTORY(state) {
      state.vehicle_search_history = []
    },
    SET_MESSAGE(state, message) {
      state.message = message
    },
    SET_MESSAGE_COMPTE_CLIENT(state, message) {
      state.message_compte_client = message
    },
    SET_PRODUCT_ADDED_TO_CART(state, product) {
      state.product_added_to_cart = product
    },
    SET_PRODUCT(state, product) {
      state.product = product
    },
    SET_PAGINATION(state, pagination) {
      state.pagination = pagination
    },
    SET_VIDEO(state, video) {
      state.video = video
    },
    SET_PRODUCTS(state, products) {
      state.products = products
    },
    SET_TOTAL_PRODUCTS(state, total) {
      state.total_products = total
    },
    SET_SEARCH_VEHICLE_PACKS(state, count) {
      state.search_vehicle_packs = count
    },
    SET_DOMAINS(state, domains) {
      state.domains = domains
    },
    SET_DOMAINS_LOADING(state, loading) {
      state.domainsLoading = loading
    },
    SET_DOMAIN_SUBMENU(state, { domainId, submenu }) {
      state.domainSubmenus = { ...state.domainSubmenus, [domainId]: submenu }
    },
    SET_DOMAIN_SUBMENU_LOADING(state, { domainId, loading }) {
      state.domainSubmenusLoading = {
        ...state.domainSubmenusLoading,
        [domainId]: loading,
      }
    },
    SET_FILTER_PRODUCT_SELECTED(state, filters) {
      state.filters_product_selected = filters
    },
    SET_LAST_FILTER_SELECTED(state, filter) {
      state.last_filter_selected = filter
    },
    PUSH_PRODUCTS(state, products) {
      state.products.push(...products)
    },
    SET_UNKNOWN_REGISTRATION(state, unknown_registration) {
      state.unknown_registration = unknown_registration
    },
    SET_REFERER(state, referrer) {
      state.referrer = referrer
    },
    SET_LOADING_PRODUCTS(state, loading) {
      state.loading_products = loading
    },
  },

  actions: {
    async setCountry({ commit, state }) {
      if (this.$nuxtCookies.get('user_country')) {
        commit('SET_COUNTRY', this.$nuxtCookies.get('user_country'))
        commit('SET_CHOSEN_COUNTRY', this.$nuxtCookies.get('user_country'))
        return
      }

      if (state.country_resolved_ssr) {
        return
      }

      const geoJsUrl = 'https://get.geojs.io/v1'

      try {
        const ipRes = await $axios.request(geoJsUrl + '/ip.json')
        const countryRes = await $axios.request(
          geoJsUrl + `/ip/country/${ipRes.data.ip}.json`
        )
        commit('SET_COUNTRY', countryRes.data.country)
      } catch {
        commit('SET_COUNTRY', 'FR')
      }
    },
    async setFreeShipping({ commit }) {
      await this.$configurationService
        .getPublic('free_shipping')
        .then(({ data }) => {
          commit('SET_FREE_SHIPPING', data.value)
        })
    },
    async setFreeShippingThresholds({ commit }) {
      const { data, methods } =
        await this.$shippingMethodService.fetchFreeShippingThresholds()

      commit('SET_FREE_SHIPPING_THRESHOLDS', data || {})
      commit('SET_FREE_SHIPPING_METHOD_THRESHOLDS', methods || {})
    },
    async setStoreOpeningHours({ commit }) {
      await this.$storeOpeningHoursService.fetch().then(({ data }) => {
        commit('SET_STORE_OPENING_HOURS', data)
      })
    },
    async setPaymentMethods({ commit }) {
      const { data } = await this.$paymentMethodService.fetch({
        limit: -1,
        total: 0,
      })

      commit(
        'SET_PAYMENT_METHODS',
        (data || []).map(({ identifier, minimum_total_ttc, is_active }) => ({
          identifier,
          minimum_total_ttc,
          is_active,
        }))
      )
    },
    async setCart({ commit }, cart) {
      await commit('SET_CART', cart)
    },

    async setUnknownRegistration({ commit }, unknown_registration) {
      commit('SET_UNKNOWN_REGISTRATION', unknown_registration)
    },

    setMessage({ commit }) {
      return this.$textService
        .fetch()
        .then(({ data }) => {
          const text = data.find(item => item.identifier === 'message-accueil')
          commit('SET_MESSAGE', text || null)
        })
        .catch(() => {})
    },

    setMessageCompteClient({ commit }) {
      this.$textService
        .fetch()
        .then(({ data }) => {
          const text = data.find(
            item => item.identifier === 'message-compte-client'
          )
          commit('SET_MESSAGE_COMPTE_CLIENT', text || null)
        })
        .catch(() => {
          commit('SET_MESSAGE_COMPTE_CLIENT', null)
        })
    },

    async setProductAddedToCart({ commit }, product) {
      commit('SET_PRODUCT_ADDED_TO_CART', product)
    },

    async setProduct({ commit }, product) {
      commit('SET_PRODUCT', product)
    },

    async setPagination({ commit }, pagination) {
      commit('SET_PAGINATION', pagination)
    },
    async setReferrer({ commit }, referrer) {
      commit('SET_REFERER', referrer)
    },

    async setVideo({ commit }, video) {
      await commit('SET_VIDEO', video)
    },

    async setFiltersProductSelected({ commit }, filters) {
      commit('SET_FILTER_PRODUCT_SELECTED', filters)
    },

    async setLastFilterSelected({ commit }, filter) {
      commit('SET_LAST_FILTER_SELECTED', filter)
    },

    async setProducts({ commit }, products) {
      commit('SET_PRODUCTS', products)
    },

    async setTotalProducts({ commit }, total) {
      commit('SET_TOTAL_PRODUCTS', total)
    },

    setSearchVehiclePacks({ commit }, count) {
      commit('SET_SEARCH_VEHICLE_PACKS', count)
    },

    async setBannerLabel({ commit }) {
      let banner_label_text
      await this.$textService
        .fetch()
        .then(({ data }) => {
          banner_label_text = data.find(
            item => item.identifier === 'home-banner-label'
          ).text
        })
        .then(() => {
          commit('SET_BANNER', banner_label_text)
        })
    },

    async setBannerUrl({ commit }) {
      await this.$configurationService
        .getPublic('home-banner-url')
        .then(({ data }) => {
          commit('SET_BANNER_URL', data.value)
        })
    },

    async setBannerStart({ commit }) {
      await this.$configurationService
        .getPublic('home-banner-start')
        .then(({ data }) => {
          commit('SET_BANNER_START', data.value)
        })
        .catch(() => {
          commit('SET_BANNER_START', null)
        })
    },

    async setBannerEnd({ commit }) {
      await this.$configurationService
        .getPublic('home-banner-end')
        .then(({ data }) => {
          commit('SET_BANNER_END', data.value)
        })
        .catch(() => {
          commit('SET_BANNER_END', null)
        })
    },

    async setClockChangeEnabled({ commit }) {
      await this.$configurationService
        .getPublic('clock-change-banner')
        .then(({ data }) => {
          commit('SET_CLOCK_CHANGE_ENABLED', String(data.value) !== '0')
        })
        .catch(() => {
          commit('SET_CLOCK_CHANGE_ENABLED', true)
        })
    },

    async setHalloweenEnabled({ commit }) {
      await this.$configurationService
        .getPublic('halloween-animations')
        .then(({ data }) => {
          commit('SET_HALLOWEEN_ENABLED', String(data.value) !== '0')
        })
        .catch(() => {
          commit('SET_HALLOWEEN_ENABLED', false)
        })
    },

    async setWelcomeOffer({ commit }) {
      await this.$codeService
        .fetchWelcomeOffer()
        .then(({ data }) => {
          commit('SET_WELCOME_OFFER', data)
        })
        .catch(() => {
          commit('SET_WELCOME_OFFER', null)
        })
    },

    async setCommercialOperation({ commit }, { preview = null } = {}) {
      await this.$commercialOperationService
        .fetchCurrent(preview === null ? {} : { at: preview })
        .then(({ data, meta }) => {
          commit('SET_COMMERCIAL_OPERATION', data || null)
          if (preview === null && Number.isFinite(meta?.server_now)) {
            commit('SET_CLOCK_NOW', meta.server_now)
          }
        })
        .catch(() => {
          commit('SET_COMMERCIAL_OPERATION', null)
        })
    },

    async setDomains({ commit, state }) {
      if (state.domains.length > 0 || state.domainsLoading) return
      commit('SET_DOMAINS_LOADING', true)
      try {
        const { data } = await this.$categoryService.fetchMenuDomains()
        commit('SET_DOMAINS', data)
      } catch {
        commit('SET_DOMAINS', [])
      } finally {
        commit('SET_DOMAINS_LOADING', false)
      }
    },

    async setDomainSubmenu({ commit, state }, domainId) {
      if (
        state.domainSubmenus[domainId] ||
        state.domainSubmenusLoading[domainId]
      )
        return
      commit('SET_DOMAIN_SUBMENU_LOADING', { domainId, loading: true })
      try {
        const { data } = await this.$categoryService.fetchMenuDomainSubmenu(
          domainId
        )
        commit('SET_DOMAIN_SUBMENU', { domainId, submenu: data })
      } catch {
        commit('SET_DOMAIN_SUBMENU', { domainId, submenu: null })
      } finally {
        commit('SET_DOMAIN_SUBMENU_LOADING', { domainId, loading: false })
      }
    },

    async pushProducts({ commit }, products) {
      commit('PUSH_PRODUCTS', products)
    },

    async setLoadingProducts({ commit }, loading) {
      commit('SET_LOADING_PRODUCTS', loading)
    },

    setFavorites({ commit, state }) {
      if (!state.auth.loggedIn) {
        return
      }
      this.$favoriteService
        .fetchAccountFavorites({ limit: 15 })
        .then(({ data }) => {
          commit('SET_FAVORITES', data)
          if (data.length === 0 && process.client) {
            this.$nuxtCookies.remove('favorite_token', { path: '/' })
          }
        })
        .catch(error => {
          if (error.response && error.response.status === 401) {
            return
          }
          throw error
        })
    },

    setVehicleSelectionTarget({ commit }, target) {
      commit('SET_VEHICLE_SELECTION_TARGET', target || null)
    },

    setCartLoadParams({ commit }, params) {
      commit('SET_CART_LOAD_PARAMS', params || null)
    },

    async applyVehicleSelectionTarget({ commit, state }, vehicle) {
      const target = state.vehicle_selection_target
      if (!target?.orderId || !vehicle?.id) return false

      commit('SET_VEHICLE_SELECTION_TARGET', null)

      try {
        if (target.replaceVehicleId && target.replaceVehicleId !== vehicle.id) {
          await this.$orderService.replaceVehicle(
            target.orderId,
            target.replaceVehicleId,
            { vehicle_id: vehicle.id }
          )
        } else {
          await this.$orderService.addVehicle(target.orderId, {
            vehicle_id: vehicle.id,
          })
        }
      } catch (error) {
        devError('[CartVehicles] Association error:', error)
        return false
      }

      return true
    },

    setSelectedVehicle({ commit }, vehicle) {
      commit('SET_SELECTED_VEHICLE', vehicle)
      if (process.client) {
        if (vehicle) {
          this.$nuxtCookies.set(
            SELECTED_VEHICLE_COOKIE_NAME,
            vehicle.id,
            cookieOpts(COOKIE_DURATION.YEAR)
          )
        } else {
          this.$nuxtCookies.remove(SELECTED_VEHICLE_COOKIE_NAME, { path: '/' })
        }
      }
    },

    loadSelectedVehicle({ commit, state }) {
      if (!process.client) return
      commit('SET_SELECTED_VEHICLE_RESTORED')
      const raw = this.$nuxtCookies.get(SELECTED_VEHICLE_COOKIE_NAME)
      if (!raw) return

      if (typeof raw === 'object' && raw.id) {
        commit('SET_SELECTED_VEHICLE', raw)
        this.$nuxtCookies.set(
          SELECTED_VEHICLE_COOKIE_NAME,
          raw.id,
          cookieOpts(COOKIE_DURATION.YEAR)
        )
        return
      }

      const vehicleId = Number(raw)
      const history =
        state.vehicle_search_history.length > 0
          ? state.vehicle_search_history
          : readVehicleHistory() || this.$nuxtCookies.get(HISTORY_COOKIE_NAME)
      const vehicle = Array.isArray(history)
        ? history.find(v => v.id === vehicleId)
        : null

      if (vehicle) {
        commit('SET_SELECTED_VEHICLE', vehicle)
      } else {
        this.$nuxtCookies.remove(SELECTED_VEHICLE_COOKIE_NAME, { path: '/' })
      }
    },

    saveVehicleHistory({ state }) {
      if (!process.client) return
      const history = state.vehicle_search_history
      if (history.length === 0) {
        clearVehicleHistory()
      } else {
        writeVehicleHistory(history.map(cleanVehicleForStorage))
      }
    },

    addToVehicleSearchHistory({ commit, dispatch }, vehicle) {
      commit('ADD_TO_VEHICLE_SEARCH_HISTORY', vehicle)
      dispatch('saveVehicleHistory')
    },

    async removeFromVehicleSearchHistory(
      { commit, state, dispatch },
      { vehicleId, search }
    ) {
      const vehicle = state.vehicle_search_history.find(
        v => v.id === vehicleId && (search === undefined || v.search === search)
      )
      const autowaysLookupId = vehicle?.autowaysLookupId

      const selected = state.selected_vehicle
      const isSelectedBeingRemoved =
        selected &&
        selected.id === vehicleId &&
        (search === undefined || selected.search === search)

      commit('REMOVE_FROM_VEHICLE_SEARCH_HISTORY', { vehicleId, search })
      dispatch('saveVehicleHistory')

      if (isSelectedBeingRemoved) {
        const next = state.vehicle_search_history[0] || null
        dispatch('setSelectedVehicle', next)
      }

      if (state.auth.loggedIn && autowaysLookupId) {
        try {
          await this.$autowaysLookupService.removeUser(autowaysLookupId)
        } catch (error) {
          devError('[VehicleHistory] Remove error:', error)
        }
      }
    },

    async clearVehicleSearchHistory({ commit, state, dispatch }) {
      if (state.auth.loggedIn) {
        const lookupIds = state.vehicle_search_history
          .filter(v => v.autowaysLookupId)
          .map(v => v.autowaysLookupId)

        if (lookupIds.length > 0) {
          await Promise.allSettled(
            lookupIds.map(id => this.$autowaysLookupService.removeUser(id))
          ).catch(error => devError('[VehicleHistory] Clear error:', error))
        }
      }

      commit('CLEAR_VEHICLE_SEARCH_HISTORY')
      dispatch('setSelectedVehicle', null)
      if (process.client) {
        clearVehicleHistory()
        this.$nuxtCookies.remove(HISTORY_COOKIE_NAME, { path: '/' })
      }
    },

    async loadVehicleSearchHistory({ commit, state }) {
      let cookieHistory = []
      if (process.client) {
        const stored = readVehicleHistory()
        const history =
          stored && stored.length > 0
            ? stored
            : this.$nuxtCookies.get(HISTORY_COOKIE_NAME)
        if (history && Array.isArray(history)) {
          cookieHistory = history
        }
      }

      const hasValidToken =
        state.auth.loggedIn &&
        state.auth.user?.id &&
        this.$auth?.strategy?.token?.get()

      if (!hasValidToken) {
        commit('SET_VEHICLE_SEARCH_HISTORY', cookieHistory)
        return
      }

      try {
        const { data } = await this.$autowaysLookupService.myHistory({
          limit: HISTORY_MAX_ITEMS,
        })

        const apiVehicles = data
          .filter(lookup => lookup.results?.length > 0)
          .map(lookup => {
            const r = lookup.results[0]
            return {
              id: r.id,
              name: r.name,
              brand: { name: r.brand_name, image: lookup.brand_image || null },
              image: lookup.vehicle_image || null,
              year_from: r.year_from,
              year_to: r.year_to,
              autowaysLookupId: lookup.id,
              search: lookup.registration,
            }
          })

        const seenKeys = new Set()
        const mergedHistory = []

        apiVehicles.forEach(apiVehicle => {
          const key = vehicleHistoryKey(apiVehicle)
          if (seenKeys.has(key)) return
          seenKeys.add(key)

          const cookieMatch = cookieHistory.find(
            cv => vehicleHistoryKey(cv) === key
          )
          if (cookieMatch) {
            mergedHistory.push({
              ...cookieMatch,
              image: cookieMatch.image || apiVehicle.image,
              brand: {
                ...apiVehicle.brand,
                ...cookieMatch.brand,
                image: cookieMatch.brand?.image || apiVehicle.brand.image,
              },
              autowaysLookupId: apiVehicle.autowaysLookupId,
            })
          } else {
            mergedHistory.push(apiVehicle)
          }
        })

        cookieHistory.forEach(cookieVehicle => {
          const key = vehicleHistoryKey(cookieVehicle)
          if (!seenKeys.has(key)) {
            mergedHistory.push(cookieVehicle)
            seenKeys.add(key)
          }
        })

        commit('SET_VEHICLE_SEARCH_HISTORY', mergedHistory)
      } catch (error) {
        const status = error.response?.status
        if (status !== 401 && status !== 404) {
          devError('[VehicleHistory] Load error:', error.message || error)
        }
        commit('SET_VEHICLE_SEARCH_HISTORY', cookieHistory)
      }
    },

    async nuxtServerInit({ commit, dispatch }, { req, query, $config }) {
      const cookieCountry = this.$nuxtCookies.get('user_country')
      const headerCountry = req?.headers?.['cf-ipcountry']
      const resolved = isIsoCountry(cookieCountry)
        ? cookieCountry
        : isIsoCountry(headerCountry)
        ? headerCountry
        : null

      if (resolved) {
        commit('SET_COUNTRY', resolved.toUpperCase())
        commit('SET_COUNTRY_RESOLVED_SSR', true)
      }

      if (isIsoCountry(cookieCountry)) {
        commit('SET_CHOSEN_COUNTRY', cookieCountry.toUpperCase())
      }

      const realNow = Date.now()
      const preview = $config?.clockPreview ? previewNow(query?.horloge) : null
      commit('SET_CLOCK_NOW', preview ?? realNow)
      commit('SET_CLOCK_OFFSET', preview === null ? 0 : preview - realNow)
      commit('SET_CLOCK_COUNTRY', resolved ? resolved.toUpperCase() : null)
      commit(
        'SET_CLOCK_CHANGE_HIDDEN',
        this.$nuxtCookies.get('hide_clock_change') || null
      )

      await Promise.allSettled([
        dispatch('setFreeShipping'),
        dispatch('setFreeShippingThresholds'),
        dispatch('setStoreOpeningHours'),
        dispatch('setPaymentMethods'),
        dispatch('setBannerLabel'),
        dispatch('setBannerUrl'),
        dispatch('setBannerStart'),
        dispatch('setBannerEnd'),
        dispatch('setClockChangeEnabled'),
        dispatch('setWelcomeOffer'),
        dispatch('setCommercialOperation', { preview }),
        isHalloweenPeriod(preview ?? realNow)
          ? dispatch('setHalloweenEnabled')
          : null,
      ])
    },
  },
}
